import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, PlusIcon, ChevronDownIcon, CheckIcon, FilterIcon } from 'lucide-react';
import { ConditionCategory, ConditionStatus, CATEGORIES, STATUSES, ASSIGNEES } from '../types/condition';
type FilterBarProps = {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  activeCategories: Set<ConditionCategory>;
  onToggleCategory: (category: ConditionCategory) => void;
  activeAssignees: Set<string>;
  onToggleAssignee: (assignee: string) => void;
  onSetAllAssignees: (all: boolean) => void;
  activeStatuses: Set<ConditionStatus>;
  onToggleStatus: (status: ConditionStatus) => void;
  onSetAllStatuses: (all: boolean) => void;
  onAddClick: () => void;
  totalCount: number;
  filteredCount: number;
};
export function FilterBar({
  searchQuery,
  onSearchChange,
  activeCategories,
  onToggleCategory,
  activeAssignees,
  onToggleAssignee,
  onSetAllAssignees,
  activeStatuses,
  onToggleStatus,
  onSetAllStatuses,
  onAddClick,
  totalCount,
  filteredCount
}: FilterBarProps) {
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [isAssigneeOpen, setIsAssigneeOpen] = useState(false);
  const categoryButtonRef = useRef<HTMLButtonElement>(null);
  const statusButtonRef = useRef<HTMLButtonElement>(null);
  const assigneeButtonRef = useRef<HTMLButtonElement>(null);
  const allCategoriesActive = activeCategories.size === CATEGORIES.length;
  const allStatusesActive = activeStatuses.size === STATUSES.length;
  const allAssigneesActive = activeAssignees.size === ASSIGNEES.length;
  const hasActiveFilters = !allCategoriesActive || !allStatusesActive || !allAssigneesActive || searchQuery;
  const getCategoryLabel = () => {
    if (allCategoriesActive) return 'All Categories';
    if (activeCategories.size === 0) return 'No Categories';
    if (activeCategories.size === 1) return Array.from(activeCategories)[0];
    return `${activeCategories.size} Categories`;
  };
  const getStatusLabel = () => {
    if (allStatusesActive) return 'All Statuses';
    if (activeStatuses.size === 0) return 'No Statuses';
    if (activeStatuses.size === 1) return Array.from(activeStatuses)[0];
    return `${activeStatuses.size} Statuses`;
  };
  const getAssigneeLabel = () => {
    if (allAssigneesActive) return 'All Assignees';
    if (activeAssignees.size === 0) return 'No Assignees';
    if (activeAssignees.size === 1) return Array.from(activeAssignees)[0];
    return `${activeAssignees.size} Assignees`;
  };
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (categoryButtonRef.current && !categoryButtonRef.current.contains(event.target as Node) && !(event.target as Element).closest('.category-dropdown')) {
        setIsCategoryOpen(false);
      }
      if (statusButtonRef.current && !statusButtonRef.current.contains(event.target as Node) && !(event.target as Element).closest('.status-dropdown')) {
        setIsStatusOpen(false);
      }
      if (assigneeButtonRef.current && !assigneeButtonRef.current.contains(event.target as Node) && !(event.target as Element).closest('.assignee-dropdown')) {
        setIsAssigneeOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleToggleAllCategories = () => {
    if (allCategoriesActive) {
      CATEGORIES.forEach(cat => {
        if (activeCategories.has(cat)) {
          onToggleCategory(cat);
        }
      });
    } else {
      CATEGORIES.forEach(cat => {
        if (!activeCategories.has(cat)) {
          onToggleCategory(cat);
        }
      });
    }
  };
  const handleToggleAllStatuses = () => {
    onSetAllStatuses(!allStatusesActive);
  };
  const handleToggleAllAssignees = () => {
    onSetAllAssignees(!allAssigneesActive);
  };
  const filterButtonClass = (isActive: boolean, isFiltered: boolean) => `
    inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-all
    ${isFiltered ? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:border-slate-400'}
  `;
  return <div className="bg-white border-b border-slate-200" data-id="element-232">
      <div className="px-6 py-3" data-id="element-233">
        <div className="flex items-center justify-between gap-4" data-id="element-234">
          {/* Left: Filter Dropdowns */}
          <div className="flex items-center gap-2" data-id="element-235">
            <FilterIcon className="w-4 h-4 text-slate-400 mr-1" data-id="element-236" />

            {/* Category Filter */}
            <div className="relative" data-id="element-237">
              <button ref={categoryButtonRef} onClick={() => setIsCategoryOpen(!isCategoryOpen)} className={filterButtonClass(isCategoryOpen, !allCategoriesActive)} data-id="element-238">
                <span data-id="element-239">{getCategoryLabel()}</span>
                <ChevronDownIcon className="w-4 h-4 opacity-50" data-id="element-240" />
              </button>

              {isCategoryOpen && <div className="category-dropdown absolute top-full left-0 mt-1.5 z-50 w-52 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden" data-id="element-241">
                  <div className="p-1.5 border-b border-slate-100" data-id="element-242">
                    <button onClick={handleToggleAllCategories} className="w-full px-3 py-2 text-sm text-left hover:bg-slate-50 rounded-md transition-colors flex items-center justify-between" data-id="element-243">
                      <span className="font-medium text-slate-900" data-id="element-244">
                        All Categories
                      </span>
                      {allCategoriesActive && <CheckIcon className="w-4 h-4 text-blue-600" data-id="element-245" />}
                    </button>
                  </div>
                  <div className="p-1.5 max-h-64 overflow-y-auto" data-id="element-246">
                    {CATEGORIES.map(category => <button key={category} onClick={() => onToggleCategory(category)} className="w-full px-3 py-2 text-sm text-left hover:bg-slate-50 rounded-md transition-colors flex items-center justify-between" data-id="element-247">
                        <span className={activeCategories.has(category) ? 'font-medium text-slate-900' : 'text-slate-600'} data-id="element-248">
                          {category}
                        </span>
                        {activeCategories.has(category) && <CheckIcon className="w-4 h-4 text-blue-600" data-id="element-249" />}
                      </button>)}
                  </div>
                </div>}
            </div>

            {/* Status Filter */}
            <div className="relative" data-id="element-250">
              <button ref={statusButtonRef} onClick={() => setIsStatusOpen(!isStatusOpen)} className={filterButtonClass(isStatusOpen, !allStatusesActive)} data-id="element-251">
                <span data-id="element-252">{getStatusLabel()}</span>
                <ChevronDownIcon className="w-4 h-4 opacity-50" data-id="element-253" />
              </button>

              {isStatusOpen && <div className="status-dropdown absolute top-full left-0 mt-1.5 z-50 w-52 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden" data-id="element-254">
                  <div className="p-1.5 border-b border-slate-100" data-id="element-255">
                    <button onClick={handleToggleAllStatuses} className="w-full px-3 py-2 text-sm text-left hover:bg-slate-50 rounded-md transition-colors flex items-center justify-between" data-id="element-256">
                      <span className="font-medium text-slate-900" data-id="element-257">
                        All Statuses
                      </span>
                      {allStatusesActive && <CheckIcon className="w-4 h-4 text-blue-600" data-id="element-258" />}
                    </button>
                  </div>
                  <div className="p-1.5 max-h-64 overflow-y-auto" data-id="element-259">
                    {STATUSES.map(status => <button key={status} onClick={() => onToggleStatus(status)} className="w-full px-3 py-2 text-sm text-left hover:bg-slate-50 rounded-md transition-colors flex items-center justify-between" data-id="element-260">
                        <span className={activeStatuses.has(status) ? 'font-medium text-slate-900' : 'text-slate-600'} data-id="element-261">
                          {status}
                        </span>
                        {activeStatuses.has(status) && <CheckIcon className="w-4 h-4 text-blue-600" data-id="element-262" />}
                      </button>)}
                  </div>
                </div>}
            </div>

            {/* Assignee Filter */}
            <div className="relative" data-id="element-263">
              <button ref={assigneeButtonRef} onClick={() => setIsAssigneeOpen(!isAssigneeOpen)} className={filterButtonClass(isAssigneeOpen, !allAssigneesActive)} data-id="element-264">
                <span data-id="element-265">{getAssigneeLabel()}</span>
                <ChevronDownIcon className="w-4 h-4 opacity-50" data-id="element-266" />
              </button>

              {isAssigneeOpen && <div className="assignee-dropdown absolute top-full left-0 mt-1.5 z-50 w-52 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden" data-id="element-267">
                  <div className="p-1.5 border-b border-slate-100" data-id="element-268">
                    <button onClick={handleToggleAllAssignees} className="w-full px-3 py-2 text-sm text-left hover:bg-slate-50 rounded-md transition-colors flex items-center justify-between" data-id="element-269">
                      <span className="font-medium text-slate-900" data-id="element-270">
                        All Assignees
                      </span>
                      {allAssigneesActive && <CheckIcon className="w-4 h-4 text-blue-600" data-id="element-271" />}
                    </button>
                  </div>
                  <div className="p-1.5 max-h-64 overflow-y-auto" data-id="element-272">
                    {ASSIGNEES.map(assignee => <button key={assignee} onClick={() => onToggleAssignee(assignee)} className="w-full px-3 py-2 text-sm text-left hover:bg-slate-50 rounded-md transition-colors flex items-center justify-between" data-id="element-273">
                        <span className={activeAssignees.has(assignee) ? 'font-medium text-slate-900' : 'text-slate-600'} data-id="element-274">
                          {assignee}
                        </span>
                        {activeAssignees.has(assignee) && <CheckIcon className="w-4 h-4 text-blue-600" data-id="element-275" />}
                      </button>)}
                  </div>
                </div>}
            </div>

            {/* Divider */}
            <div className="w-px h-6 bg-slate-200 mx-1" data-id="element-276"></div>

            {/* Results Count */}
            <div className="text-sm text-slate-500" data-id="element-277">
              {filteredCount === totalCount ? <span data-id="element-278">{totalCount} conditions</span> : <span className="text-blue-600 font-medium" data-id="element-279">
                  {filteredCount} of {totalCount}
                </span>}
            </div>
          </div>

          {/* Right: Search and Add */}
          <div className="flex items-center gap-3" data-id="element-280">
            <div className="relative" data-id="element-281">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-282" />
              <input type="text" value={searchQuery} onChange={e => onSearchChange(e.target.value)} placeholder="Search conditions..." className="pl-9 pr-4 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white w-56 transition-all" data-id="element-283" />
            </div>

            <button onClick={onAddClick} className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-sm transition-all" data-id="element-284">
              <PlusIcon className="w-4 h-4" data-id="element-285" />
              Add Condition
            </button>
          </div>
        </div>
      </div>
    </div>;
}